import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { HStack, Icon, Text, useToast } from '@chakra-ui/react';
import { BellIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { apiGet } from '../../lib/api';
import { useNotificationConfig } from '../../hooks/useNotifications';
import { useNotificationRealtime } from '../../hooks/useNotificationRealtime';
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences';
import { useAttentionBurst, burstPopupKey } from '../../hooks/useAttentionBurst';
import { isStudyingRoute } from '../../lib/notificationAttentionPolicy';
import soundEffectsService from '../../services/soundEffectsService';
import type { NotificationItem } from '../../types/notification';
import {
  notifyBindingToServiceWorker,
  reconcilePushSubscription,
  registerNotificationServiceWorker,
} from '../../lib/notificationPush';

interface NotificationContextValue {
  userId: string | null;
  enabled: boolean;
  streamEnabled: boolean;
  pushAvailable: boolean;
  registerOpenPanel: (handler: (() => void) | null) => void;
  requestOpenPanel: () => void;
}

const noop = () => undefined;

const NotificationContext = createContext<NotificationContextValue>({
  userId: null,
  enabled: false,
  streamEnabled: false,
  pushAvailable: false,
  registerOpenPanel: noop,
  requestOpenPanel: noop,
});

export function useNotificationContext(): NotificationContextValue {
  return useContext(NotificationContext);
}

/** In-app popup body for a freshly created notification. */
function AttentionToast({
  item,
  onOpen,
}: {
  item: NotificationItem;
  onOpen?: () => void;
}): JSX.Element {
  return (
    <HStack
      data-testid="notification-attention-toast"
      align="flex-start"
      spacing={3}
      bg="white"
      boxShadow="lg"
      borderRadius="md"
      borderWidth="1px"
      px={4}
      py={3}
      maxW="360px"
      cursor={onOpen ? 'pointer' : undefined}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={
        onOpen
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <Icon as={BellIcon} color="blue.500" mt="3px" />
      <Text fontSize="sm">{item.title}</Text>
    </HStack>
  );
}

/**
 * Aggregate popup for a same kind+class burst: one visible notice that states how
 * many notifications arrived inside the burst window, instead of a popup per row
 * (plan: "Burst cùng loại+lớp trong 60 giây hiện popup tổng hợp 'Có {count} thông báo mới'").
 *
 * The copy goes through i18next's own `{{count}}` interpolation, so the catalogue
 * stays the single source of truth and no manual placeholder surgery is needed.
 */
function BurstSummaryToast({
  count,
  t,
  onOpen,
}: {
  count: number;
  t: TFunction;
  onOpen?: () => void;
}): JSX.Element {
  return (
    <HStack
      data-testid="notification-burst-summary"
      align="flex-start"
      spacing={3}
      bg="white"
      boxShadow="lg"
      borderRadius="md"
      borderWidth="1px"
      px={4}
      py={3}
      maxW="360px"
      cursor={onOpen ? 'pointer' : undefined}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={
        onOpen
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <Icon as={BellIcon} color="blue.500" mt="3px" />
      <Text fontSize="sm">{t('notification.burstSummary', { count })}</Text>
    </HStack>
  );
}

/**
 * Resolves the authenticated user and notification readiness once, near the app
 * root. When the feature is unavailable/disabled it exposes enabled=false so the
 * bell is not mounted and no stream/poll is opened. Also owns the SSE connection
 * and the foreground in-app popup/sound, narrowed per item by the attention
 * policy (never widened).
 */
export default function NotificationProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const { data: me } = useQuery({
    queryKey: ['user-me'],
    queryFn: () => apiGet<{ id: number }>('/api/me'),
    staleTime: 5 * 60 * 1000,
  });
  const userId = me?.id != null ? String(me.id) : null;

  const { data: config, isError } = useNotificationConfig();
  const enabled = !isError && config?.enabled === true;
  const { data: prefData } = useNotificationPreferences(userId, enabled);

  const location = useLocation();
  const toast = useToast();

  // Keep the latest preference config in a ref so the attention callback always
  // reads current settings without re-subscribing the stream.
  const prefRef = useRef(prefData?.config);
  prefRef.current = prefData?.config;

  // "Studying" is a real route signal, never the user preference alone: on its
  // own the muteWhileStudying preference must not silence ordinary pages.
  const studying = isStudyingRoute(location.pathname);

  const burst = useAttentionBurst();
  const { t } = useTranslation();

  const openPanelRef = useRef<(() => void) | null>(null);
  const registerOpenPanel = useCallback((handler: (() => void) | null) => {
    openPanelRef.current = handler;
  }, []);
  const requestOpenPanel = useCallback(() => {
    openPanelRef.current?.();
  }, []);
  // Ids this tab has already created. `toast.isActive` reads Chakra's store, and
  // `notify` always PREPENDS a record, so a batch of synchronous callbacks would
  // create one DOM popup per row before any of them became observable. This set is
  // updated synchronously, so one group yields exactly one popup per batch.
  const createdToastIdsRef = useRef<Set<string>>(new Set());

  useNotificationRealtime({
    userId,
    enabled,
    getConfig: () => prefRef.current ?? null,
    studying,
    // The realtime hook already ran the per-item policy, so these callbacks only
    // fire for channels the policy allowed for that specific item.
    onSound: (volume) => {
      soundEffectsService.playWithOptions('notification', { enabled: true, volume });
    },
    // Claim the global sound allowance before it plays, so a rate-rejected sound
    // never burns the persisted dedupe key (the alerter commits only when true).
    soundAccepted: () => burst.acceptSound(Date.now()),
    onToast: (item) => {
      // Same kind+class inside the window collapses into one aggregate carrying
      // the real count; the burst budget additionally caps concurrent popups.
      const { group, shown } = burst.acceptToast(item, Date.now());
      // Budget exhausted: the row is already counted in the aggregate, so it must
      // not open another popup. A later row re-evaluates the slot, so nothing is
      // stuck refused once a popup closes.
      if (!shown) return;
      // ONE id per group for the group's whole life, so a plain first row and the
      // aggregate that follows never exist as two separate DOM popups (which would
      // leak a second, uncounted slot).
      const id = burstPopupKey(group);
      const options = {
        position: 'top-right' as const,
        duration: 8000,
        isClosable: true,
        // Releases exactly THIS group's slot — a stray/duplicated callback for a
        // group that owns no slot cannot free another group's budget.
        onCloseComplete: () => {
          createdToastIdsRef.current.delete(id);
          burst.closePopup(group.key);
        },
        render: () => {
          const openFromToast = () => {
            requestOpenPanel();
            toast.close(id);
          };
          return group.count > 1 ? (
            <BurstSummaryToast count={group.count} t={t} onOpen={openFromToast} />
          ) : (
            <AttentionToast item={item} onOpen={openFromToast} />
          );
        },
      };
      // Create once, then update in place. The synchronous set is the source of
      // truth for "already created", because Chakra's `isActive` cannot see a
      // toast created earlier in the same synchronous batch.
      if (createdToastIdsRef.current.has(id) || toast.isActive(id)) {
        toast.update(id, options);
      } else {
        createdToastIdsRef.current.add(id);
        toast({ id, ...options });
      }
    },
  });

  useEffect(() => {
    if (userId == null) return;
    void (async () => {
      await registerNotificationServiceWorker();
      await notifyBindingToServiceWorker(Number(userId));
      await reconcilePushSubscription(Number(userId));
    })();
    return () => {
      // Account switch/unmount: drop the in-memory binding in the worker so the
      // previous user's payloads can never be shown for the new session.
      void notifyBindingToServiceWorker(null);
    };
  }, [userId]);

  const contextValue = useMemo(
    () => ({
      userId,
      enabled,
      streamEnabled: enabled && config?.streamEnabled === true,
      pushAvailable: enabled && config?.pushAvailable === true,
      registerOpenPanel,
      requestOpenPanel,
    }),
    [userId, enabled, config?.streamEnabled, config?.pushAvailable, registerOpenPanel, requestOpenPanel],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}
